// @ts-nocheck
import { forwardRef, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { Paper } from '../../../ui';
import { FadeIn, HoverLift } from '../../../animations';
import type { KpiCardProps } from '../types';
import { getContrastingTextColor } from '../../../../utils/color';

/**
 * KpiCard - Basic KPI card component
 *
 * Displays a single key performance indicator with icon, title, and value.
 * Supports animated counting, dark mode, and multiple size variants.
 */
const KpiCard = forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      value,
      description,
      icon,
      color = 'blue',
      size = 'md',
      variant = 'default',
      gradientVariant = 'primary',
      onClick,
      animate = false,
      animationDuration = 2000,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const gradient = theme.other?.gradients?.[gradientVariant];

    const [displayValue, setDisplayValue] = useState(animate ? 0 : value);

    // Calculate text color for gradient background
    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    // Size configurations
    const sizes = {
      xs: {
        padding: 'xs',
        iconSize: 28,
        titleSize: '10px',
        valueSize: 'lg',
        gap: 'xs',
      },
      sm: {
        padding: 'sm',
        iconSize: 32,
        titleSize: 'xs',
        valueSize: 'xl',
        gap: 'sm',
      },
      md: {
        padding: 'md',
        iconSize: 40,
        titleSize: 'sm',
        valueSize: '2rem',
        gap: 'md',
      },
      lg: {
        padding: 'lg',
        iconSize: 48,
        titleSize: 'md',
        valueSize: '2.5rem',
        gap: 'lg',
      },
      xl: {
        padding: 'xl',
        iconSize: 56,
        titleSize: 'lg',
        valueSize: '3rem',
        gap: 'xl',
      },
    };

    const currentSize = sizes[size];

    // Animated counter effect
    useEffect(() => {
      if (!animate || typeof value !== 'number') {
        setDisplayValue(value);
        return;
      }

      const startValue = 0;
      const endValue = value;
      const duration = animationDuration;
      const startTime = Date.now();

      const animateValue = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);

        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(startValue + (endValue - startValue) * easeOut);

        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animateValue);
        } else {
          setDisplayValue(endValue);
        }
      };

      requestAnimationFrame(animateValue);
    }, [value, animate, animationDuration]);

    const paperVariant =
      variant === 'gradient' ? 'gradient' : variant === 'outlined' ? 'outlined' : 'default';

    const isGradient = variant === 'gradient';
    const isOutlined = variant === 'outlined';
    const isDark = colorScheme === 'dark';

    return (
      <FadeIn>
        <HoverLift disabled={!onClick}>
          <Paper
            ref={ref}
            variant={paperVariant}
            gradientVariant={gradientVariant}
            gradientOpacity={isGradient ? 1 : 0.1}
            p={currentSize.padding}
            onClick={onClick}
            className={className}
            style={{
              cursor: onClick ? 'pointer' : 'default',
              height: '100%',
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={currentSize.gap} style={{ flex: 1 }}>
                <Text
                  size={currentSize.titleSize}
                  c={isGradient ? autoTextColor : 'dimmed'}
                  tt="uppercase"
                  fw={700}
                  style={{ letterSpacing: '0.5px' }}
                >
                  {title}
                </Text>

                <Text
                  size={currentSize.valueSize}
                  fw={700}
                  c={isGradient ? autoTextColor : undefined}
                  style={{
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {displayValue}
                </Text>

                {description && (
                  <Text
                    size="xs"
                    c={isGradient ? autoTextColor : 'dimmed'}
                    style={{ opacity: isGradient ? 0.9 : 1 }}
                  >
                    {description}
                  </Text>
                )}
              </Stack>

              {icon && (
                <ThemeIcon
                  size={currentSize.iconSize}
                  radius="md"
                  variant={isGradient ? 'transparent' : 'light'}
                  color={isGradient ? undefined : color}
                  style={{
                    background: isGradient ? 'rgba(255,255,255,0.2)' : undefined,
                    color: isGradient ? autoTextColor : undefined,
                  }}
                >
                  {icon}
                </ThemeIcon>
              )}
            </Group>
          </Paper>
        </HoverLift>
      </FadeIn>
    );
  }
);

KpiCard.displayName = 'KpiCard';

export default KpiCard;
